import { useEffect, useState } from "react";
import { databases, DATABASE_ID, COLLECTIONS } from "../lib/appwriteClient";
import { Query } from "appwrite";
import { Trophy, Medal, Award, Zap } from "lucide-react";

export function LiveScores() {
  const [scoresByEvent, setScoresByEvent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScores();
  }, []);

  const fetchScores = async () => {
    try {
      const eventsRes = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.EVENTS,
        [Query.equal("is_active", true), Query.orderAsc("order")],
      );

      const scoresRes = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.SCORES,
        [Query.orderAsc("position")],
      );

      const events = eventsRes.documents;
      const scores = scoresRes.documents;

      const grouped = {};
      events.forEach((event) => {
        const eventScores = scores.filter((s) => s.event_id === event.$id);
        if (eventScores.length > 0) {
          grouped[event.$id] = { event, scores: eventScores };
        }
      });

      setScoresByEvent(grouped);
    } catch (error) {
      console.error("Error fetching scores:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPositionIcon = (position) => {
    const glowStyle = {
      filter: "drop-shadow(0 0 6px rgba(255,255,255,0.5))",
    };

    switch (position) {
      case 1:
        return (
          <Trophy
            className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-300"
            style={glowStyle}
          />
        );
      case 2:
        return (
          <Medal
            className="w-5 h-5 sm:w-6 sm:h-6 text-gray-200"
            style={glowStyle}
          />
        );
      case 3:
        return (
          <Award
            className="w-5 h-5 sm:w-6 sm:h-6 text-orange-300"
            style={glowStyle}
          />
        );
      default:
        return (
          <Award
            className="w-5 h-5 sm:w-6 sm:h-6 text-green-200"
            style={glowStyle}
          />
        );
    }
  };

  const getPositionColor = (position) => {
    switch (position) {
      case 1:
        return "bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-white"; // Gold
      case 2:
        return "bg-gradient-to-r from-gray-300 via-gray-400 to-gray-500 text-white"; // Silver
      case 3:
        return "bg-gradient-to-r from-orange-400 via-amber-600 to-orange-700 text-white"; // Bronze
      default:
        return "bg-gradient-to-r from-emerald-600 via-green-700 to-emerald-800 text-white"; // Dark green
    }
  };

  if (loading) {
    return (
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <div className="animate-pulse text-lg font-medium">
            Loading scores...
          </div>
        </div>
      </section>
    );
  }

  if (Object.keys(scoresByEvent).length === 0) {
    return (
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <Trophy className="w-14 h-14 mx-auto mb-4 text-green-300" />
          <p className="text-lg sm:text-xl text-gray-600">
            No scores yet. Check back during the event!
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full mb-4">
            <Zap className="w-5 h-5" />
            <span className="font-semibold">Live Updates</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Live Scores
          </h2>
        </div>

        {/* EVENTS */}
        <div className="space-y-8">
          {Object.values(scoresByEvent).map(({ event, scores }) => (
            <div
              key={event.$id}
              className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition duration-300"
            >
              {/* EVENT HEADER */}
              <div className="px-5 py-4 sm:px-6 sm:py-5 rounded-t-xl bg-gradient-to-r from-emerald-700 via-green-800 to-emerald-900 flex items-center justify-between">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white tracking-wide">
                  {event.name}
                </h3>

                <span className="text-xs sm:text-sm bg-white/20 px-3 py-1 rounded-full text-white">
                  Live
                </span>
              </div>

              {/* SCORES */}
              <div className="p-4 sm:p-6 space-y-3">
                {scores.map((score) => (
                  <div
                    key={score.$id}
                    className={`${getPositionColor(
                      score.position,
                    )} p-4 rounded-lg`}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      {/* LEFT */}
                      <div className="flex items-center gap-3">
                        {getPositionIcon(score.position)}
                        <div>
                          <div className="font-bold text-sm sm:text-base">
                            {score.winner_name}
                          </div>
                          {score.batch_name && (
                            <div className="text-xs sm:text-sm opacity-90">
                              {score.batch_name}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* RIGHT */}
                      <div className="text-left sm:text-right">
                        {score.score && (
                          <div className="font-bold text-base sm:text-lg">
                            {score.score}
                          </div>
                        )}
                        <div className="text-sm">#{score.position}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
